import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Drawer from "@mui/material/Drawer";
import Icon from "../../Components/Icons";
import { color, fontFamily, radius } from "../../Components/Theme";
import { useNavItems, isActive } from "./navItems";

const MAX_VISIBLE = 5;

const Bar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  background: ${color("surface")};
  border-top: 1px solid ${color("border")};
  padding-bottom: env(safe-area-inset-bottom);
  font-family: ${() => fontFamily()};
`;

const tabStyles = (p) => `
  flex: 1 1 0;
  min-width: 0;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 2px;
  border: 0;
  background: transparent;
  text-decoration: none;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  cursor: pointer;
  color: ${p.$active ? color("primary") : color("text-muted")};
  text-align: center;
  span { max-width: 100%; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow-wrap: anywhere; }
`;

const Tab = styled(Link)`
  ${tabStyles}
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: -2px; }
`;
const TabButton = styled.button`
  ${tabStyles}
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: -2px; }
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 28px;
  border-radius: 999px;
  background: ${(p) => (p.$active ? color("primary-light") : "transparent")};
`;

const SheetItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 52px;
  padding: 0 16px;
  border-radius: ${() => radius("control")}px;
  text-decoration: none;
  font-family: ${() => fontFamily()};
  font-size: 15px;
  font-weight: 600;
  color: ${(p) => (p.$active ? color("primary") : color("text"))};
  background: ${(p) => (p.$active ? color("primary-light") : "transparent")};
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: 2px; }
`;

const BottomNav = ({ selected }) => {
  const items = useNavItems({ mergeCareRecords: true });
  const [moreOpen, setMoreOpen] = useState(false);

  const needsMore = items.length > MAX_VISIBLE;
  const visible = needsMore ? items.slice(0, MAX_VISIBLE - 1) : items;
  const overflow = needsMore ? items.slice(MAX_VISIBLE - 1) : [];
  const moreActive = overflow.some((i) => isActive(selected, i.href));

  return (
    <>
      <Bar aria-label="Main">
        {visible.map((value) => {
          const active = isActive(selected, value.href);
          return (
            <Tab key={value.href} to={value.href} $active={active} aria-current={active ? "page" : undefined}>
              <Pill $active={active}>
                <Icon $icon={value.icon} $size="22px" $color={active ? "primary" : "text-muted"} />
              </Pill>
              <span>{value.name}</span>
            </Tab>
          );
        })}
        {needsMore && (
          <TabButton type="button" $active={moreActive} onClick={() => setMoreOpen(true)} aria-haspopup="dialog" aria-expanded={moreOpen}>
            <Pill $active={moreActive}>
              <Icon $icon="MoreHoriz" $size="22px" $color={moreActive ? "primary" : "text-muted"} />
            </Pill>
            <span>More</span>
          </TabButton>
        )}
      </Bar>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{ sx: { borderRadius: `${radius("card")}px ${radius("card")}px 0 0`, pb: "env(safe-area-inset-bottom)" } }}
      >
        <nav aria-label="More" style={{ padding: 12 }}>
          {overflow.map((value) => {
            const active = isActive(selected, value.href);
            return (
              <SheetItem
                key={value.href}
                to={value.href}
                $active={active}
                aria-current={active ? "page" : undefined}
                onClick={() => setMoreOpen(false)}
              >
                <Icon $icon={value.icon} $size="24px" $color={active ? "primary" : "text-muted"} />
                <span>{value.name}</span>
              </SheetItem>
            );
          })}
        </nav>
      </Drawer>
    </>
  );
};

export default BottomNav;
